import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { Clock, Flame, Dumbbell, ChevronDown, CheckCircle2, PersonStanding } from "lucide-react";
import { BRAND_LOGO } from "@/lib/brandAssets";
import MuscleFilterDialog, { musclesForPlan, TOTAL_MUSCLES } from "@/components/library/MuscleFilterDialog";
import { getWorkoutImage } from "@/utils/workoutImages";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * Library — branded showcase of workout templates grouped by days/week.
 *
 * Data: pulls every `WorkoutTemplate` from the existing entity API. We don't
 * fetch a separate "library" endpoint because the templates themselves are
 * the source of truth (admin-created samples + trainer-created plans). The
 * card art is generated client-side from the `goal` field — we don't ship
 * hero photos per template, so each goal maps to a `goalTheme` that stamps
 * the card with the right gradient + geometric ornament (hexagons /
 * chevrons / dot-matrix) mirroring the design comp.
 *
 * Filters: Duration + Difficulty — both shrink the list before grouping,
 * so an empty bucket disappears entirely. Filter values are kept in local
 * state — no URL params yet (KISS, can promote later if the user shares
 * library links).
 */

/** Estimate session length from `exercises[]`. Each exercise = sets × 45s of
 *  work + sets × rest_seconds, summed and divided by 60. Falls back to a
 *  sensible 45min if exercises array is missing/empty so cards never show
 *  "0 min". */
function estimateDurationMin(exercises = []) {
  if (!exercises.length) return 45;
  let total = 0;
  for (const ex of exercises) {
    const sets = Number(ex.sets) || 3;
    const rest = Number(ex.rest_seconds) || 60;
    total += sets * 45 + sets * rest;
  }
  return Math.max(15, Math.round(total / 60));
}

/** Bucket a duration in minutes into one of the four UX-friendly bands the
 *  filter chip exposes. */
function durationBucket(min) {
  if (min < 30) return "15-30";
  if (min < 45) return "30-45";
  if (min < 60) return "45-60";
  return "60+";
}

/** Count distinct training days from the embedded exercises. Falls back to
 *  `days_per_week` when the template author didn't tag day labels. */
function workoutCount(t) {
  const days = new Set((t.exercises || []).map((e) => e.day).filter(Boolean));
  return days.size || t.days_per_week || 1;
}

const goalLabels = {
  strength: "Build Strength",
  hypertrophy: "Build Muscle",
  endurance: "Endurance",
  fat_loss: "Get Lean",
  general_fitness: "Overall Fitness",
};

const splitLabels = {
  1: "Full Body Split",
  2: "Upper / Lower Split",
  3: "Push / Pull / Legs",
  4: "Upper / Lower x2",
  5: "Bro Split",
  6: "PPL x2",
  7: "Daily Drive",
};

/** Goal-themed gradient + ornament. Each card draws a stylised decoration
 *  via SVG instead of an athlete photo (we don't have hero images per
 *  template). Mirrors the design-comp variations: hexagons (Get in Shape),
 *  chevrons (Get Lean), dot matrix + play (Overall Fitness). */
const goalThemes = {
  strength: {
    gradient: "linear-gradient(110deg, #021024 0%, #0B2A5B 55%, #1E40AF 100%)",
    accent: "text-sky-400",
    ornament: (
      <svg viewBox="0 0 200 120" className="absolute inset-y-0 right-0 h-full w-1/2 opacity-60" preserveAspectRatio="xMaxYMid slice">
        {[...Array(7)].map((_, i) => (
          <polygon
            key={i}
            points="30,5 55,18 55,42 30,55 5,42 5,18"
            transform={`translate(${100 + i * 12}, ${20 + (i % 2) * 28}) scale(${0.4 + i * 0.08})`}
            fill="none"
            stroke="#38bdf8"
            strokeWidth="1.3"
            opacity={0.18 + i * 0.07}
          />
        ))}
      </svg>
    ),
  },
  hypertrophy: {
    gradient: "linear-gradient(110deg, #050B1E 0%, #0A2547 55%, #1E3A8A 100%)",
    accent: "text-sky-300",
    ornament: (
      <svg viewBox="0 0 240 120" className="absolute inset-y-0 right-0 h-full w-2/3 opacity-70" preserveAspectRatio="xMaxYMid slice">
        {[...Array(5)].map((_, i) => (
          <path
            key={i}
            d={`M${40 + i * 30},20 L${85 + i * 30},60 L${40 + i * 30},100`}
            stroke="#38bdf8"
            strokeWidth={i === 4 ? 4 : 3}
            fill="none"
            opacity={0.15 + i * 0.15}
          />
        ))}
      </svg>
    ),
  },
  fat_loss: {
    gradient: "linear-gradient(110deg, #02061A 0%, #0E2655 55%, #1D4ED8 100%)",
    accent: "text-sky-400",
    ornament: (
      <svg viewBox="0 0 240 120" className="absolute inset-y-0 right-0 h-full w-2/3 opacity-65" preserveAspectRatio="xMaxYMid slice">
        {[...Array(5)].map((_, i) => (
          <path
            key={i}
            d={`M${40 + i * 30},20 L${85 + i * 30},60 L${40 + i * 30},100`}
            stroke="#0ea5e9"
            strokeWidth={i === 4 ? 5 : 3}
            fill="none"
            opacity={0.18 + i * 0.16}
          />
        ))}
      </svg>
    ),
  },
  endurance: {
    gradient: "linear-gradient(110deg, #021024 0%, #082351 55%, #1E40AF 100%)",
    accent: "text-sky-400",
    ornament: (
      <svg viewBox="0 0 240 120" className="absolute inset-y-0 right-0 h-full w-2/3 opacity-65" preserveAspectRatio="xMaxYMid slice">
        {[...Array(80)].map((_, i) => {
          const x = (i % 10) * 18 + 60;
          const y = Math.floor(i / 10) * 14 + 12;
          return <circle key={i} cx={x} cy={y} r="2" fill="#38bdf8" opacity={0.25 + (i % 5) * 0.1} />;
        })}
        <path d="M150,40 L180,60 L150,80 Z" fill="#0ea5e9" opacity="0.7" />
        <path d="M180,40 L210,60 L180,80 Z" fill="#0ea5e9" opacity="0.55" />
      </svg>
    ),
  },
  general_fitness: {
    gradient: "linear-gradient(110deg, #02061A 0%, #0A2347 55%, #1E40AF 100%)",
    accent: "text-sky-400",
    ornament: (
      <svg viewBox="0 0 240 120" className="absolute inset-y-0 right-0 h-full w-2/3 opacity-70" preserveAspectRatio="xMaxYMid slice">
        {[...Array(80)].map((_, i) => {
          const x = (i % 10) * 18 + 60;
          const y = Math.floor(i / 10) * 14 + 12;
          return <circle key={i} cx={x} cy={y} r="2" fill="#38bdf8" opacity={0.25 + (i % 5) * 0.1} />;
        })}
        <path d="M150,40 L180,60 L150,80 Z" fill="#0ea5e9" opacity="0.75" />
        <path d="M180,40 L210,60 L180,80 Z" fill="#0ea5e9" opacity="0.5" />
      </svg>
    ),
  },
};

const DIFFICULTY_OPTIONS = ["beginner", "intermediate", "advanced"];
const DURATION_OPTIONS = ["15-30", "30-45", "45-60", "60+"];

function FilterChip({ icon: Icon, label, count, children }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={`library-filter-${label.toLowerCase()}`}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-sky-500/30 bg-sky-500/[0.06] text-sky-200 text-xs sm:text-sm font-medium hover:border-sky-400/60 hover:bg-sky-500/10 transition whitespace-nowrap shrink-0"
      >
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <span>
          {label}
          {typeof count === "number" ? ` (${count})` : ""}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="bg-[#0B1220] border-sky-500/30 text-white"
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PlanCard({ plan, isActive = false }) {
  const goalKey = (plan.goal || "general_fitness").toLowerCase();
  const theme = goalThemes[goalKey] || goalThemes.general_fitness;
  const wcount = workoutCount(plan);
  const dur = estimateDurationMin(plan.exercises);
  const split = splitLabels[plan.days_per_week] || `${plan.days_per_week}-Day Split`;
  // Big title that mirrors the design comp — uppercase, with the LAST word
  // painted in the sky accent so each card reads like a poster.
  const goalText = (goalLabels[goalKey] || plan.name || "Workout").toUpperCase();
  const goalWords = goalText.split(" ");
  const tail = goalWords.pop();
  const head = goalWords.join(" ");

  return (
    <Link
      to={`/library/${plan.id}`}
      data-testid={`library-plan-card-${plan.id}`}
      className={`block relative rounded-2xl overflow-hidden transition shadow-[0_8px_30px_-12px_rgba(56,189,248,0.35)] active:scale-[0.99] ${
        isActive
          ? "border-2 border-emerald-400/70 hover:border-emerald-300"
          : "border border-sky-500/20 hover:border-sky-400/50"
      }`}
      style={{ background: theme.gradient }}
    >
      {/* Background Hero Image */}
      <img
        src={getWorkoutImage(plan)}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center opacity-30 pointer-events-none transition-transform duration-500 hover:scale-105"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#020617]/95 via-[#020617]/85 to-[#020617]/40 pointer-events-none" />

      {/* Ornamental SVG decoration on the right half */}
      {theme.ornament}

      {/* Active badge — shown when at least one day of this template is
          currently in the user's weekly schedule. Sits over the ornament
          on the top-right corner so it doesn't compete with the goal logo
          on the top-left. */}
      {isActive && (
        <span
          data-testid={`library-active-badge-${plan.id}`}
          className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/95 text-white text-[10px] font-bold uppercase tracking-widest shadow-[0_0_18px_-4px_rgba(16,185,129,0.9)]"
        >
          <CheckCircle2 className="w-3 h-3" /> Active
        </span>
      )}

      <div className="relative z-10 p-4 sm:p-5 min-h-[150px] flex flex-col justify-between">
        <div className="flex items-center gap-2">
          <img
            src={BRAND_LOGO}
            alt=""
            className="h-6 object-contain opacity-95"
            draggable="false"
          />
        </div>

        <div>
          <h3 className="font-display font-extrabold text-2xl sm:text-3xl leading-[1.05] tracking-tight">
            {head ? (
              <>
                <span className="text-white">{head}</span>{" "}
              </>
            ) : null}
            <span className={theme.accent}>{tail}</span>
          </h3>
          <div className="mt-1 h-[3px] w-10 rounded bg-sky-400" />
          <p className="mt-2 text-sm text-white/85 font-medium truncate">{plan.name || split}</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold uppercase tracking-wider text-white/90">
              <Flame className="w-3 h-3" /> {plan.difficulty || "all levels"}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold uppercase tracking-wider text-white/90">
              <Clock className="w-3 h-3" /> {dur} min
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold uppercase tracking-wider text-white/90">
              <Dumbbell className="w-3 h-3" /> {wcount} workout{wcount === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Library() {
  const { user } = useAuth();
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: () => base44.entities.WorkoutTemplate.list(),
  });

  // The user's current weekday → plan_id map. We use it together with the
  // user's WorkoutPlan list to figure out which library templates are
  // currently "active" (i.e. at least one of their generated plans is
  // still sitting in the weekly schedule).
  const { data: weeklyWorkoutPlan = null } = useQuery({
    queryKey: ["userWeeklyWorkoutPlan", user?.id],
    queryFn: () =>
      base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: user.id }).then(
        (r) => r[0] || null,
      ),
    enabled: !!user?.id,
  });
  const { data: userWorkoutPlans = [] } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list("-created_date"),
    enabled: !!user?.id,
  });

  // Set of template ids currently materialised into the user's weekly
  // schedule. Built by:
  //   1. Collecting every plan_id sitting in the weekday slots.
  //   2. Looking up each plan and parsing its `external_id` — assignments
  //      from this Library are tagged `library:{template_id}:{weekday}`,
  //      so the substring between the two colons is the template id.
  const activeTemplateIds = useMemo(() => {
    if (!weeklyWorkoutPlan) return new Set();
    const DAYS = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const dayPlanIds = new Set();
    for (const d of DAYS) {
      const pid = weeklyWorkoutPlan[d];
      if (pid) dayPlanIds.add(pid);
    }
    if (dayPlanIds.size === 0) return new Set();
    const out = new Set();
    for (const wp of userWorkoutPlans) {
      if (!dayPlanIds.has(wp.id)) continue;
      const ext = wp.external_id || "";
      if (ext.startsWith("library:")) {
        const parts = ext.split(":");
        if (parts.length >= 3) out.add(parts[1]);
      }
    }
    return out;
  }, [weeklyWorkoutPlan, userWorkoutPlans]);

  const [diffSel, setDiffSel] = useState(new Set());
  const [durSel, setDurSel] = useState(new Set());
  const [muscleOff, setMuscleOff] = useState(new Set()); // muscles deselected = HIDDEN
  const [muscleDialogOpen, setMuscleDialogOpen] = useState(false);

  // For muscle-name fallback: map exercise name → catalogue row (which has `target`).
  const { data: allExercises = [] } = useQuery({
    queryKey: ["exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
  });
  const exerciseByName = useMemo(() => {
    const m = new Map();
    for (const e of allExercises) {
      if (e?.name) m.set(e.name.trim().toLowerCase(), e);
    }
    return m;
  }, [allExercises]);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (diffSel.size && !diffSel.has((t.difficulty || "").toLowerCase())) return false;
      if (durSel.size) {
        const b = durationBucket(estimateDurationMin(t.exercises));
        if (!durSel.has(b)) return false;
      }
      // Muscle exclusion: if any of the plan's muscles is in the
      // deselected set, hide the card.
      if (muscleOff.size) {
        const m = musclesForPlan(t, exerciseByName);
        for (const off of muscleOff) {
          if (m.has(off)) return false;
        }
      }
      return true;
    });
  }, [templates, diffSel, durSel, muscleOff, exerciseByName]);

  // Group by days_per_week, sorted asc so "1 Day a Week" appears first.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const k = t.days_per_week || 1;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(t);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [filtered]);

  const toggle = (setter, sel) => (val) => {
    const next = new Set(sel);
    next.has(val) ? next.delete(val) : next.add(val);
    setter(next);
  };

  return (
    <div className="space-y-5" data-testid="library-page">
      {/* Page header */}
      <div className="flex items-end justify-between">
        <h1 className="text-4xl sm:text-5xl font-display font-extrabold tracking-tight">Library</h1>
      </div>

      {/* Filter row — horizontal scroll on phones so the chips never wrap. */}
      <div className="-mx-1 px-1 flex items-center gap-2 overflow-x-auto pb-1">
        <FilterChip icon={Clock} label="Duration" count={durSel.size || undefined}>
          <DropdownMenuLabel className="text-white/60 text-xs uppercase tracking-wider">
            Duration
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          {DURATION_OPTIONS.map((d) => (
            <DropdownMenuCheckboxItem
              key={d}
              checked={durSel.has(d)}
              onCheckedChange={() => toggle(setDurSel, durSel)(d)}
              className="text-white"
              data-testid={`library-duration-${d}`}
            >
              {d} min
            </DropdownMenuCheckboxItem>
          ))}
        </FilterChip>
        <FilterChip icon={Flame} label="Difficulty" count={diffSel.size || undefined}>
          <DropdownMenuLabel className="text-white/60 text-xs uppercase tracking-wider">
            Difficulty
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          {DIFFICULTY_OPTIONS.map((d) => (
            <DropdownMenuCheckboxItem
              key={d}
              checked={diffSel.has(d)}
              onCheckedChange={() => toggle(setDiffSel, diffSel)(d)}
              className="text-white capitalize"
              data-testid={`library-difficulty-${d}`}
            >
              {d}
            </DropdownMenuCheckboxItem>
          ))}
        </FilterChip>
        {/* Muscle filter — uses a dialog (silhouette picker) instead of a
            dropdown, so it lives outside <FilterChip> but matches the same
            visual chip styling. */}
        <button
          type="button"
          onClick={() => setMuscleDialogOpen(true)}
          data-testid="library-filter-muscles"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-sky-500/30 bg-sky-500/[0.06] text-sky-200 text-xs sm:text-sm font-medium hover:border-sky-400/60 hover:bg-sky-500/10 transition whitespace-nowrap shrink-0"
        >
          <PersonStanding className="w-3.5 h-3.5 shrink-0" />
          <span>
            Muscles ({TOTAL_MUSCLES - muscleOff.size})
          </span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
        </button>
      </div>

      <MuscleFilterDialog
        open={muscleDialogOpen}
        onOpenChange={setMuscleDialogOpen}
        deselected={muscleOff}
        onChange={setMuscleOff}
      />

      {isLoading && (
        <p className="text-sm text-white/50">Loading the library…</p>
      )}

      {!isLoading && filtered.length === 0 && (
        <div
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center"
          data-testid="library-empty-state"
        >
          <p className="text-sm text-white/70">No plans match those filters yet.</p>
          <p className="text-xs text-white/40 mt-1">Try clearing one to see more.</p>
        </div>
      )}

      {grouped.map(([days, plans]) => (
        <section key={days} className="space-y-3" data-testid={`library-section-${days}`}>
          <h2 className="text-base sm:text-lg font-display font-extrabold tracking-tight">
            {days} Day{days === 1 ? "" : "s"} a Week
          </h2>
          <div className="space-y-3">
            {plans.map((p) => (
              <PlanCard key={p.id} plan={p} isActive={activeTemplateIds.has(p.id)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
